import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faApple, faGoogle } from "@fortawesome/free-brands-svg-icons";

import SEOHead from "../components/common/SEOHead";
import { useArticles } from "../contexts/ArticlesContext";
import SITE, { absoluteUrl } from "../data/siteMetadata";
import {
	ONBOARDING_URL,
	normalizeSwapstampParams,
	swapstampLanguages,
	swapstampVerticals,
} from "../data/swapstampLanding";

import "./styles/swapstampLanding.css";

const localeByLanguage = {
	it: "it_IT",
	en: "en_US",
	es: "es_ES",
};

const languageLabels = {
	it: "🇮🇹",
	en: "🇬🇧",
	es: "🇪🇸",
};

const interfaceCopy = {
	it: {
		brandLine: "Digital loyalty cards per business locali",
		navHow: "Come funziona",
		navProof: "Risultati",
		navStart: "Try it now",
		technicalBlog: "Technical blog",
		articlesTitle: "Technical blog",
		articlesText: "Mettiamo la tecnologia al centro e la documentiamo nel technical blog.",
		articleEmpty: "Gli articoli tecnici saranno disponibili qui.",
		trialLabel: "2 settimane gratis",
		priceLine: "Poi da 9.99 al mese. Nessuna tessera da stampare.",
		heroBadge: "Carta fedelta digitale con QR",
		trustOne: "Nessuna app da scaricare",
		trustTwo: "Setup guidato",
		trustThree: "Pronto per Apple Wallet e Google Wallet",
		verticalLabel: "Scegli settore",
		languageLabel: "Lingua",
		metricsTitle: "Segnali di utilizzo reali",
		metricsText:
			"Dati operativi da merchant attivi: clienti iscritti, punti assegnati e premi riscattati dentro un flusso digitale.",
		casesTitle: "Merchant gia attivi",
		casesText:
			"Swapstamp e gia usato in contesti reali. La pagina mostra il prodotto per quello che deve fare: portare ritorni, ridurre carta e rendere misurabile la loyalty.",
		flowTitle: "Il prodotto in azione",
		flowText: "Il flusso e semplice: card nel Wallet, scansione alla cassa, punti aggiornati subito.",
		platformTitle: "Una loyalty card digitale, non un altro gestionale",
		platformText:
			"Il merchant controlla regole, premi e dashboard. Il cliente usa il telefono, mostra la card Wallet e vede i progressi senza installare nulla.",
		walletKicker: "Apple Wallet + Google Wallet",
		pricingTitle: "Prezzo semplice, rischio basso",
		pricingText: "2 settimane gratis, poi da 9.99 al mese. Una soluzione leggera per piccoli business locali, non un programma enterprise.",
		faqTitle: "Domande rapide",
		technicalTitle: "Costruito per partire veloce e scalare",
		technicalItems: [
			"QR flow per iscrizione, punti e premio",
			"Autenticazione con Google o email",
			"Backend serverless con AWS Lambda, API Gateway e DynamoDB",
			"Asset e pagine distribuiti con S3 e CloudFront",
		],
		footerText: "Swapstamp aiuta business locali a lanciare programmi fedelta digitali in modo rapido, misurabile e senza carta.",
	},
	en: {
		brandLine: "Digital loyalty cards for local businesses",
		navHow: "How it works",
		navProof: "Results",
		navStart: "Try it now",
		technicalBlog: "Technical blog",
		articlesTitle: "Technical blog",
		articlesText: "We put technology at the center and document it in the technical blog.",
		articleEmpty: "Technical articles will appear here.",
		trialLabel: "2-week free trial",
		priceLine: "Then from 9.99 per month. No paper cards to print.",
		heroBadge: "Digital loyalty card with QR",
		trustOne: "No customer app download",
		trustTwo: "Guided setup",
		trustThree: "Ready for Apple Wallet and Google Wallet",
		verticalLabel: "Choose market",
		languageLabel: "Language",
		metricsTitle: "Real usage signals",
		metricsText:
			"Operational data from active merchants: joined cards, given points and completed rewards inside a digital flow.",
		casesTitle: "Active merchants",
		casesText:
			"Swapstamp is already used in real local contexts. This page shows the product for what it must do: bring customers back, reduce paper and make loyalty measurable.",
		flowTitle: "The product in action",
		flowText: "The flow is simple: Wallet card, checkout scan, points updated instantly.",
		platformTitle: "A digital loyalty card, not another management system",
		platformText:
			"The merchant controls rules, rewards and dashboard. The customer uses their phone, shows the Wallet card and sees progress without installing anything.",
		walletKicker: "Apple Wallet + Google Wallet",
		pricingTitle: "Simple pricing, low risk",
		pricingText: "2-week free trial, then from 9.99 per month. A lightweight solution for small local businesses, not an enterprise loyalty program.",
		faqTitle: "Quick questions",
		technicalTitle: "Built to launch fast and scale",
		technicalItems: [
			"QR flow for joining, points and rewards",
			"Authentication with Google or email",
			"Serverless backend with AWS Lambda, API Gateway and DynamoDB",
			"Assets and pages delivered through S3 and CloudFront",
		],
		footerText: "Swapstamp helps local businesses launch digital loyalty programs quickly, measurably and without paper.",
	},
	es: {
		brandLine: "Tarjetas de fidelizacion digitales para negocios locales",
		navHow: "Como funciona",
		navProof: "Resultados",
		navStart: "Try it now",
		technicalBlog: "Technical blog",
		articlesTitle: "Technical blog",
		articlesText: "Ponemos la tecnologia en el centro y la documentamos en el technical blog.",
		articleEmpty: "Los articulos tecnicos apareceran aqui.",
		trialLabel: "2 semanas gratis",
		priceLine: "Despues desde 9.99 al mes. Sin tarjetas de papel.",
		heroBadge: "Tarjeta digital con QR",
		trustOne: "Sin app para el cliente",
		trustTwo: "Configuracion guiada",
		trustThree: "Listo para Apple Wallet y Google Wallet",
		verticalLabel: "Elige sector",
		languageLabel: "Idioma",
		metricsTitle: "Senales reales de uso",
		metricsText:
			"Datos operativos de merchants activos: tarjetas creadas, puntos asignados y recompensas completadas dentro de un flujo digital.",
		casesTitle: "Merchants activos",
		casesText:
			"Swapstamp ya se usa en contextos locales reales. La pagina muestra el producto para lo que debe hacer: traer clientes de vuelta, reducir papel y medir la fidelizacion.",
		flowTitle: "El producto en accion",
		flowText: "El flujo es simple: tarjeta Wallet, escaneo en caja y puntos actualizados al instante.",
		platformTitle: "Una tarjeta digital, no otro sistema de gestion",
		platformText:
			"El merchant controla reglas, recompensas y dashboard. El cliente usa el movil, muestra la tarjeta Wallet y ve su progreso sin instalar nada.",
		walletKicker: "Apple Wallet + Google Wallet",
		pricingTitle: "Precio simple, bajo riesgo",
		pricingText: "2 semanas gratis, despues desde 9.99 al mes. Una solucion ligera para pequenos negocios locales, no un programa enterprise.",
		faqTitle: "Preguntas rapidas",
		technicalTitle: "Creado para lanzar rapido y escalar",
		technicalItems: [
			"Flujo QR para alta, puntos y recompensa",
			"Autenticacion con Google o email",
			"Backend serverless con AWS Lambda, API Gateway y DynamoDB",
			"Assets y paginas distribuidos con S3 y CloudFront",
		],
		footerText: "Swapstamp ayuda a negocios locales a lanzar programas digitales de fidelizacion de forma rapida, medible y sin papel.",
	},
};

const impactMetrics = [
	{ value: "68%", label: "Return increase", note: "Early signal from recurring customers who would lose paper cards" },
	{ value: "32%", label: "Sales growth", note: "Recurring sales growth observed across active usage" },
	{ value: "100%", label: "Less paper", note: "Paper and plastic loyalty cards replaced by QR cards" },
	{ value: "3.5x", label: "Faster flow", note: "Less time spent managing loyalty activity manually" },
];

const successCases = [
	{
		name: "Tazze Pazze",
		logo: "/swapstamp/logo-tazze-pazze.png",
		vendorId: "3a7e1153-4288-4243-a17a-fcd50839e41f",
		joinCard: "108",
		givePoint: "1,489",
		rewards: "154",
	},
	{
		name: "The Habitat Club",
		logo: "/swapstamp/logo-habitat-club.png",
		vendorId: "68b74519-2811-45a9-91f5-7c42a6b58b28",
		joinCard: "188",
		givePoint: "1,293",
		rewards: "81",
	},
];

const articleVisuals = [
	{
		match: ["terraform", "infrastructure", "cloudfront", "route53", "s3"],
		src: "/projects_logo/terraform.png",
		label: "Infrastructure",
	},
	{
		match: ["cognito", "auth", "authentication", "login"],
		src: "/projects_logo/cognito.png",
		label: "Authentication",
	},
	{
		match: ["lambda", "backend", "api", "gateway", "serverless"],
		src: "/projects_logo/awslambda.png",
		label: "Backend",
	},
	{
		match: ["wallet", "apple", "google"],
		src: "/projects_logo/wallet.png",
		label: "Wallet",
	},
	{
		match: ["swapstamp", "vendor", "customer", "loyalty", "card"],
		src: "/projects_logo/swapstamp.svg",
		label: "Swapstamp",
	},
];

const getArticleVisual = (article, index = 0) => {
	const keywords = Array.isArray(article?.keywords) ? article.keywords.join(" ") : "";
	const articleText = `${article?.title || ""} ${article?.description || ""} ${keywords}`.toLowerCase();
	const matchedVisual = articleVisuals.find((visual) => visual.match.some((keyword) => articleText.includes(keyword)));
	return matchedVisual || articleVisuals[index % articleVisuals.length];
};

const SwapstampLanding = () => {
	const params = useParams();
	const location = useLocation();
	const { articles, fetchArticles } = useArticles();
	const { vertical, lang } = normalizeSwapstampParams(params.vertical, params.lang);
	const verticalData = swapstampVerticals[vertical];
	const copy = verticalData.copy[lang];
	const ui = interfaceCopy[lang];
	const path = params.vertical && params.lang ? `/swapstamp/landing/${vertical}/${lang}` : "/swapstamp/landing/coffeshop/en";
	const canonicalPath = location.pathname === "/swapstamp/landing" ? "/swapstamp/landing/coffeshop/en" : path;
	const technicalArticles = useMemo(() => articles.slice(0, 4), [articles]);
	const [selectedArticleId, setSelectedArticleId] = useState("");
	const [selectedArticle, setSelectedArticle] = useState(null);
	const [isArticleLoading, setIsArticleLoading] = useState(false);
	const selectedArticleIndex = technicalArticles.findIndex((article) => article.articleId === selectedArticleId);
	const selectedArticleVisual = getArticleVisual(selectedArticle, selectedArticleIndex >= 0 ? selectedArticleIndex : 0);

	useEffect(() => {
		if (!location.hash) {
			window.scrollTo(0, 0);
			return;
		}

		const scrollTimer = window.setTimeout(() => {
			const target = document.getElementById(location.hash.replace("#", ""));
			if (target) {
				target.scrollIntoView({ block: "start" });
			}
		}, 250);

		return () => window.clearTimeout(scrollTimer);
	}, [vertical, lang, location.hash]);

	useEffect(() => {
		if (articles.length === 0) {
			fetchArticles();
		}
	}, [articles.length, fetchArticles]);

	useEffect(() => {
		if (!selectedArticleId && technicalArticles.length > 0) {
			setSelectedArticleId(technicalArticles[0].articleId);
		}
	}, [selectedArticleId, technicalArticles]);

	useEffect(() => {
		if (!selectedArticleId) {
			return;
		}

		const listArticle = technicalArticles.find((article) => article.articleId === selectedArticleId);
		setSelectedArticle(listArticle || null);

		if (listArticle?.body) {
			return;
		}

		setIsArticleLoading(true);
		fetch(`https://api.northwestwind.org/articles/${selectedArticleId}`)
			.then((response) => {
				if (!response.ok) {
					throw new Error("Unable to load article");
				}
				return response.json();
			})
			.then((article) => setSelectedArticle(article))
			.catch(() => setSelectedArticle(listArticle || null))
			.finally(() => setIsArticleLoading(false));
	}, [selectedArticleId, technicalArticles]);

	const alternates = useMemo(() => {
		const localized = swapstampLanguages.map((language) => ({
			hrefLang: language,
			href: `/swapstamp/landing/${vertical}/${language}`,
		}));

		return [
			...localized,
			{ hrefLang: "x-default", href: `/swapstamp/landing/${vertical}/en` },
		];
	}, [vertical]);

	const jsonLd = useMemo(() => {
		const pageUrl = absoluteUrl(canonicalPath);
		const softwareId = `${SITE.url}/swapstamp#software`;
		const videoObjects = [
			{
				title: copy.giveTitle,
				file: verticalData.videos.givePoint,
				description: `${copy.giveTitle} with Swapstamp for ${verticalData.audience[lang]}.`,
			},
			{
				title: copy.feedbackTitle,
				file: verticalData.videos.feedback,
				description: `${copy.feedbackTitle} with Swapstamp for ${verticalData.audience[lang]}.`,
			},
		].map((video) => ({
			"@context": "https://schema.org",
			"@type": "VideoObject",
			name: video.title,
			description: video.description,
			uploadDate: "2026-08-25",
			contentUrl: absoluteUrl(video.file),
			thumbnailUrl: absoluteUrl("/swapstamp/swapstamp.png"),
			inLanguage: lang,
		}));

		return [
			{
				"@context": "https://schema.org",
				"@type": "SoftwareApplication",
				"@id": softwareId,
				name: "Swapstamp",
				applicationCategory: "BusinessApplication",
				operatingSystem: "Web, iOS Wallet, Android Wallet",
				url: "https://app.swapstamp.com/hub/onboarding",
				description:
					"Digital loyalty card software for local businesses using QR cards, dashboard controls, rewards, feedback and wallet integrations.",
				creator: { "@id": `${SITE.url}/#person` },
			},
			{
				"@context": "https://schema.org",
				"@type": "Service",
				"@id": `${pageUrl}#service`,
				name: copy.seoTitle,
				description: copy.seoDescription,
				serviceType: "Digital loyalty card setup",
				audience: verticalData.audience[lang],
				provider: { "@id": softwareId },
			},
			{
				"@context": "https://schema.org",
				"@type": "WebPage",
				"@id": `${pageUrl}#webpage`,
				url: pageUrl,
				name: copy.seoTitle,
				description: copy.seoDescription,
				inLanguage: lang,
				mainEntity: { "@id": `${pageUrl}#service` },
			},
			{
				"@context": "https://schema.org",
				"@type": "BreadcrumbList",
				itemListElement: [
					{
						"@type": "ListItem",
						position: 1,
						name: "Swapstamp",
						item: `${SITE.url}/swapstamp`,
					},
					{
						"@type": "ListItem",
						position: 2,
						name: verticalData.label[lang],
						item: pageUrl,
					},
				],
			},
			...videoObjects,
		];
	}, [canonicalPath, copy, lang, verticalData]);

	return (
		<React.Fragment>
			<SEOHead
				title={copy.seoTitle}
				description={copy.seoDescription}
				keywords={[
					"digital loyalty card",
					"digital loyalty cards",
					"QR loyalty card",
					"customer loyalty program",
					"local business loyalty",
					copy.kicker,
					verticalData.label.en,
				]}
				path={canonicalPath}
				image="/swapstamp/swapstamp.png"
				language={lang}
				locale={localeByLanguage[lang]}
				alternates={alternates}
				jsonLd={jsonLd}
			/>

			<main className="swapstamp-landing">
				<header className="swapstamp-topbar">
					<a className="swapstamp-brand" href="#top" aria-label="Swapstamp">
						<img src="/swapstamp.svg" alt="" />
						<span>
							<strong>Swapstamp</strong>
							<small>{ui.brandLine}</small>
						</span>
					</a>
					<nav className="swapstamp-nav" aria-label="Swapstamp page navigation">
						<a href="#how">{ui.navHow}</a>
						<a href="#proof">{ui.navProof}</a>
						<a href="#technical-blog">{ui.technicalBlog}</a>
						<a className="nav-cta" href={ONBOARDING_URL} target="_blank" rel="noreferrer">
							{ui.navStart}
						</a>
					</nav>
					<div className="swapstamp-language-island" aria-label={ui.languageLabel}>
						{swapstampLanguages.map((language) => (
							<Link
								className={language === lang ? "active" : ""}
								key={language}
								to={`/swapstamp/landing/${vertical}/${language}`}
							>
								{languageLabels[language]}
							</Link>
						))}
					</div>
				</header>

				<section className="swapstamp-hero" id="top">
					<div className="swapstamp-hero-copy">
						<div className="swapstamp-pill">{ui.heroBadge}</div>
						<h1>{copy.h1}</h1>
						<p className="swapstamp-lead">{copy.lead}</p>
						<div className="swapstamp-actions">
							<a className="swapstamp-primary" href={ONBOARDING_URL} target="_blank" rel="noreferrer">
								{copy.primaryCta}
							</a>
							<a className="swapstamp-secondary" href="#flow">
								{copy.secondaryCta}
							</a>
						</div>
						<div className="swapstamp-trust-row" aria-label="Product highlights">
							<span>{ui.trustOne}</span>
							<span>{ui.trustTwo}</span>
							<span>{ui.trustThree}</span>
						</div>
						<div className="swapstamp-wallet-badges" aria-label="Wallet support">
							<span>
								<FontAwesomeIcon icon={faApple} />
								Apple Wallet
							</span>
							<span>
								<FontAwesomeIcon icon={faGoogle} />
								Google Wallet
							</span>
						</div>
						<div className="swapstamp-price-banner" aria-label="Free trial and price">
							<strong>{ui.trialLabel}</strong>
							<span>{ui.priceLine}</span>
						</div>
					</div>

					<div className="swapstamp-hero-stage" aria-label="Swapstamp product videos">
						<div className="swapstamp-phone phone-main">
							<video src={verticalData.videos.givePoint} autoPlay muted loop playsInline preload="auto" />
							<div className="phone-caption">{copy.giveTitle}</div>
						</div>
						<div className="swapstamp-phone phone-secondary">
							<video src={verticalData.videos.feedback} autoPlay muted loop playsInline preload="auto" />
							<div className="phone-caption">{copy.feedbackTitle}</div>
						</div>
					</div>
				</section>

				<section className="swapstamp-checkout-steps" aria-label={copy.stepsTitle}>
					<div>
						<p className="swapstamp-section-kicker">{copy.stepsTitle}</p>
						<h2>{copy.outcomeTitle}</h2>
					</div>
					<div className="checkout-step-grid">
						{copy.steps.map((step, index) => (
							<div className="checkout-step" key={step}>
								<strong>{index + 1}</strong>
								<span>{step}</span>
							</div>
						))}
					</div>
				</section>

				<section className="swapstamp-outcomes">
					<div className="outcomes-copy">
						<p className="swapstamp-section-kicker">{copy.kicker}</p>
						<h2>{copy.outcomeTitle}</h2>
						<p>{copy.proofLine}</p>
					</div>
					<div className="outcome-list">
						{copy.outcomes.map((outcome) => (
							<div className="outcome-item" key={outcome}>
								<span />
								<p>{outcome}</p>
							</div>
						))}
					</div>
				</section>

				<section className="swapstamp-flow" id="flow">
					<div className="swapstamp-section-heading">
						<p>{ui.flowTitle}</p>
						<h2>{ui.flowText}</h2>
					</div>
					<div className="flow-grid">
						<article className="flow-panel">
							<video src={verticalData.videos.givePoint} autoPlay muted loop playsInline preload="auto" />
							<h3>{copy.giveTitle}</h3>
						</article>
						<article className="flow-panel">
							<video src={verticalData.videos.feedback} autoPlay muted loop playsInline preload="auto" />
							<h3>{copy.feedbackTitle}</h3>
						</article>
					</div>
				</section>

				<section className="swapstamp-how" id="how">
					<div className="how-copy">
						<p className="swapstamp-section-kicker">{copy.stepsTitle}</p>
						<h2>{ui.platformTitle}</h2>
						<p>{ui.platformText}</p>
					</div>
					<div className="how-steps">
						{copy.steps.map((step, index) => (
							<div className="how-step" key={step}>
								<strong>{String(index + 1).padStart(2, "0")}</strong>
								<span>{step}</span>
							</div>
						))}
					</div>
				</section>

				<section className="swapstamp-wallet-section">
					<div className="wallet-copy">
						<p className="swapstamp-section-kicker">{ui.walletKicker}</p>
						<h2>{copy.walletTitle}</h2>
						<p>{copy.walletText}</p>
					</div>
					<div className="wallet-card-stack" aria-label="Apple Wallet and Google Wallet support">
						<div className="wallet-logo-card">
							<FontAwesomeIcon icon={faApple} />
							<span>Apple Wallet</span>
						</div>
						<div className="wallet-logo-card">
							<FontAwesomeIcon icon={faGoogle} />
							<span>Google Wallet</span>
						</div>
					</div>
				</section>

				<section className="swapstamp-proof" id="proof">
					<div className="swapstamp-section-heading proof-heading">
						<p>{ui.metricsTitle}</p>
						<h2>{ui.metricsText}</h2>
					</div>
					<div className="metric-grid">
						{impactMetrics.map((metric) => (
							<article className="metric-card" key={metric.label}>
								<strong>{metric.value}</strong>
								<span>{metric.label}</span>
								<p>{metric.note}</p>
							</article>
						))}
					</div>

					<div className="case-intro">
						<h2>{ui.casesTitle}</h2>
						<p>{ui.casesText}</p>
					</div>
					<div className="case-grid">
						{successCases.map((vendor) => (
							<article className="case-card" key={vendor.vendorId}>
								<div className="case-top">
									<img src={vendor.logo} alt={`${vendor.name} logo`} />
									<div>
										<h3>{vendor.name}</h3>
										<p>{vendor.vendorId}</p>
									</div>
								</div>
								<div className="case-stats">
									<div>
										<strong>{vendor.joinCard}</strong>
										<span>Join card</span>
									</div>
									<div>
										<strong>{vendor.givePoint}</strong>
										<span>Give point</span>
									</div>
									<div>
										<strong>{vendor.rewards}</strong>
										<span>Rewards</span>
									</div>
								</div>
							</article>
						))}
					</div>
				</section>

				<section className="swapstamp-articles" id="technical-blog">
					<div className="swapstamp-article-intro">
						<div>
							<p>{ui.articlesTitle}</p>
							<h2>{ui.articlesText}</h2>
						</div>
						<div className="swapstamp-article-visual" aria-hidden="true">
							<img src="/swapstamp.svg" alt="" />
							<span>API</span>
							<span>Wallet</span>
							<span>QR</span>
						</div>
					</div>
					{technicalArticles.length > 0 ? (
						<div className="swapstamp-article-module">
							<div className="swapstamp-article-list" aria-label="Technical article list">
								{technicalArticles.map((article, index) => {
									const visual = getArticleVisual(article, index);
									return (
										<button
											className={article.articleId === selectedArticleId ? "active" : ""}
											key={article.articleId}
											onClick={() => setSelectedArticleId(article.articleId)}
											type="button"
										>
											<img src={visual.src} alt="" />
											<span>{article.date}</span>
											<strong>{article.title}</strong>
											<small>{article.description}</small>
										</button>
									);
								})}
							</div>
							<div className="swapstamp-article-reader">
								{selectedArticle ? (
									<React.Fragment>
										<div className="reader-visual">
											<img src={selectedArticleVisual.src} alt="" />
											<span>{selectedArticleVisual.label}</span>
										</div>
										<div className="reader-meta">{selectedArticle.date}</div>
										<h3>{selectedArticle.title}</h3>
										<p>{selectedArticle.description}</p>
										<div className="reader-body">
											{isArticleLoading ? (
												<span>Loading article...</span>
											) : selectedArticle.body ? (
												<div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedArticle.body) }} />
											) : (
												<span>{selectedArticle.description}</span>
											)}
										</div>
									</React.Fragment>
								) : null}
							</div>
						</div>
					) : (
						<div className="swapstamp-article-empty">{ui.articleEmpty}</div>
					)}
				</section>

				<section className="swapstamp-fit">
					<div>
						<p className="swapstamp-section-kicker">{verticalData.label[lang]}</p>
						<h2>{copy.fitTitle}</h2>
						<p>{copy.fitText}</p>
					</div>
					<div className="technical-box">
						<h3>{ui.technicalTitle}</h3>
						<ul>
							{ui.technicalItems.map((item) => (
								<li key={item}>{item}</li>
							))}
						</ul>
					</div>
				</section>

				<section className="swapstamp-pricing-faq">
					<div className="pricing-card">
						<p className="swapstamp-section-kicker">{ui.pricingTitle}</p>
						<strong>{ui.trialLabel}</strong>
						<span>{ui.priceLine}</span>
						<p>{ui.pricingText}</p>
						<a className="swapstamp-primary" href={ONBOARDING_URL} target="_blank" rel="noreferrer">
							{ui.navStart}
						</a>
					</div>
					<div className="faq-card">
						<p className="swapstamp-section-kicker">{ui.faqTitle}</p>
						{copy.faqs.map(([question, answer]) => (
							<div className="faq-item" key={question}>
								<h3>{question}</h3>
								<p>{answer}</p>
							</div>
						))}
					</div>
				</section>

				<section className="swapstamp-switchboard" aria-label="Swapstamp landing variants">
					<div>
						<span>{ui.verticalLabel}</span>
						<div className="switch-links">
							{Object.entries(swapstampVerticals).map(([key, item]) => (
								<Link className={key === vertical ? "active" : ""} key={key} to={`/swapstamp/landing/${key}/${lang}`}>
									{item.label[lang]}
								</Link>
							))}
						</div>
					</div>
				</section>

				<section className="swapstamp-final" id="start">
					<p className="swapstamp-section-kicker">Swapstamp onboarding</p>
					<h2>{copy.finalTitle}</h2>
					<p>{copy.finalText}</p>
					<a className="swapstamp-primary" href={ONBOARDING_URL} target="_blank" rel="noreferrer">
						{copy.primaryCta}
					</a>
				</section>

				<footer className="swapstamp-footer">
					<span>Swapstamp</span>
					<p>
						{ui.footerText} <a href="#technical-blog">{ui.technicalBlog}</a>
					</p>
				</footer>
			</main>
		</React.Fragment>
	);
};

export default SwapstampLanding;
